import React from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../styles';
import { ALLOCATION_BAR, BREAKPOINTS } from '../../constants/ui';

interface AllocationBarProps {
  percentage: number;
  width?: number;
  height?: number;
}

const AllocationBar: React.FC<AllocationBarProps> = React.memo(
  ({ percentage, width = ALLOCATION_BAR.DEFAULT_WIDTH, height = ALLOCATION_BAR.DEFAULT_HEIGHT }) => {
    const fillWidth = Math.max((percentage / 100) * width, percentage > 0 ? ALLOCATION_BAR.MIN_FILL_WIDTH : 0);

    const containerStyles = css`
      display: flex;
      align-items: center;
      gap: ${spacing.milli};
      margin-top: 4px;
      width: 100%;
    `;

    const barBackgroundStyles = css`
      height: ${height}px;
      background-color: var(--border-color);
      border-radius: 2px;
      overflow: hidden;
      width: ${width}px;
      flex-shrink: 0;
    `;

    const barFillStyles = css`
      height: 100%;
      background: linear-gradient(90deg, ${colors.petrolBlue} 0%, #4a9eff 100%);
      border-radius: 2px;
      transition: width 0.3s ease;
      width: ${fillWidth}px;

      html[data-theme='dark'] & {
        background: linear-gradient(90deg, ${colors.skyBlue} 0%, #6bb6ff 100%);
      }
    `;

    const percentageTextStyles = css`
      ${fontStyles.flea};
      color: var(--secondary-text);
      font-weight: 500;
      min-width: 35px;
      text-align: right;
      font-size: 11px;

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        font-size: 10px;
        min-width: 30px;
      }
    `;

    const responsiveContainerStyles = css`
      ${containerStyles}

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        gap: 6px;
      }
    `;

    const responsiveBarStyles = css`
      ${barBackgroundStyles}

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        width: ${Math.min(width, ALLOCATION_BAR.MOBILE_WIDTH)}px;
      }
    `;

    return (
      <div css={responsiveContainerStyles}>
        <div
          css={responsiveBarStyles}
          role="progressbar"
          aria-valuenow={Math.round(percentage)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Portfolio allocation: ${Math.round(percentage)} percent`}
        >
          <div css={barFillStyles} />
        </div>
        <span css={percentageTextStyles} aria-hidden="true">
          {Math.round(percentage)}%
        </span>
      </div>
    );
  },
);

export default AllocationBar;
